import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { api } from '../lib/api';
import { AlertCircle, FileText, GitBranch, Lightbulb, Clock, CheckCircle2, AlertTriangle, Mail, Loader2, User, Filter, UserCheck, ThumbsUp, ThumbsDown, Undo2, Zap, Laptop, PhoneCall, ChevronRight, Timer } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';

export const DashboardPage = () => {
  const { user } = useAuth();
  const { isModuleEnabled, loading: subscriptionLoading } = useSubscription();
  const [stats, setStats] = useState({
    incidents: 0,
    requests: 0,
    changes: 0,
    problems: 0,
    laptopProvisioning: 0
  });
  const [recentTickets, setRecentTickets] = useState([]);
  const [myCreatedIncidents, setMyCreatedIncidents] = useState([]);
  const [myIncidentsFilter, setMyIncidentsFilter] = useState({ status: '', priority: '' });
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  
  // Pending Approvals state
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [approvalComments, setApprovalComments] = useState('');
  const [activeCallbacks, setActiveCallbacks] = useState([]);
  const [processingApproval, setProcessingApproval] = useState(false);
  
  // Expedite functionality
  const [isExpedited, setIsExpedited] = useState(false);
  const [showExpediteConfirm, setShowExpediteConfirm] = useState(false);
  const [expediteConfirmed, setExpediteConfirmed] = useState(false);

  const isAdminOrSuper = user?.role === 'admin' || user?.role === 'super_admin';
  const isItilUser = user?.role === 'itil_staff' || user?.role === 'admin' || user?.role === 'super_admin';
  // All users can potentially see approvals if they are designated approvers
  // The backend will filter to show only relevant approvals for each user
  const canSeeApprovals = true; // Let backend determine what approvals to show

  const handlePollEmails = async () => {
    try {
      setPolling(true);
      const response = await api.pollEmails();
      const result = response.data;
      toast.success(`Email poll complete: ${result.tickets_created || 0} tickets created, ${result.comments_added || 0} comments added`);
      // Refresh dashboard data after polling
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to poll emails');
    } finally {
      setPolling(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
    if (canSeeApprovals) {
      loadPendingApprovals();
    }
    loadActiveCallbacks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadActiveCallbacks = async () => {
    try {
      const res = await api.get('/voice/my-callbacks');
      setActiveCallbacks(res.data.active || []);
    } catch {
      // silent — user may not have callbacks
    }
  };

  // Load pending approvals
  const loadPendingApprovals = async () => {
    try {
      setApprovalsLoading(true);
      const response = await api.getPendingApprovals();
      setPendingApprovals(response.data || []);
    } catch (error) {
      console.error('Failed to load pending approvals:', error);
    } finally {
      setApprovalsLoading(false);
    }
  };

  // Handle approval decision
  const handleApprovalDecision = async (decision) => {
    if (!selectedApproval) return;
    
    const decisionLabels = {
      approved: 'approved',
      refer_back: 'referred back',
      rejected: 'rejected'
    };
    
    try {
      setProcessingApproval(true);
      await api.approveTask(selectedApproval.id, decision, approvalComments, expediteConfirmed);
      const expediteMsg = expediteConfirmed ? ' (EXPEDITED)' : '';
      toast.success(`Request ${decisionLabels[decision]} successfully${expediteMsg}`);
      setApprovalDialogOpen(false);
      setSelectedApproval(null);
      setApprovalComments('');
      setIsExpedited(false);
      setExpediteConfirmed(false);
      loadPendingApprovals(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to process request`);
    } finally {
      setProcessingApproval(false);
    }
  };
  
  // Handle expedite checkbox change
  const handleExpediteChange = (checked) => {
    if (checked) {
      setShowExpediteConfirm(true);
    } else {
      setIsExpedited(false);
      setExpediteConfirmed(false);
    }
  };
  
  // Confirm expedite
  const confirmExpedite = () => {
    setIsExpedited(true);
    setExpediteConfirmed(true);
    setShowExpediteConfirm(false);
  };

  // Open approval dialog
  const openApprovalDialog = (approval) => {
    setSelectedApproval(approval);
    setApprovalComments('');
    setIsExpedited(false);
    setExpediteConfirmed(false);
    setApprovalDialogOpen(true);
  };

  // Load my created incidents when filter changes
  useEffect(() => {
    if (isItilUser) {
      loadMyCreatedIncidents();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myIncidentsFilter, isItilUser]);

  const loadMyCreatedIncidents = async () => {
    try {
      const response = await api.getMyCreatedIncidents(
        myIncidentsFilter.status || null,
        myIncidentsFilter.priority || null
      );
      setMyCreatedIncidents(response.data || []);
    } catch (error) {
      console.error('Failed to load my created incidents:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      const [incidents, requests, changes, problems, laptopProvisioning] = await Promise.all([
        api.getIncidents(),
        api.getServiceRequests(),  // Use service-requests endpoint
        api.getChangeRequests(),   // Use change-requests endpoint
        api.getProblems(),
        api.getLaptopProvisioningDashboard().catch(() => ({ data: [] }))
      ]);

      setStats({
        incidents: incidents.data.length,
        requests: requests.data.length,
        changes: changes.data.length,
        problems: problems.data.length,
        laptopProvisioning: laptopProvisioning.data?.length || 0
      });

      const allTickets = [
        ...incidents.data.map(t => ({ ...t, type: 'incident' })),
        ...requests.data.map(t => ({ ...t, type: 'request' })),
        ...changes.data.map(t => ({ ...t, type: 'change' })),
        ...problems.data.map(t => ({ ...t, type: 'problem' }))
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);

      setRecentTickets(allTickets);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Define stat cards with their module keys
  const allStatCards = [
    { title: 'Incidents', value: stats.incidents, icon: AlertCircle, color: 'text-red-600', link: '/incidents', moduleKey: 'incidents' },
    { title: 'Requests', value: stats.requests, icon: FileText, color: 'text-blue-600', link: '/my-requests', moduleKey: 'service_requests' },
    { title: 'Changes', value: stats.changes, icon: GitBranch, color: 'text-purple-600', link: '/changes', moduleKey: 'changes' },
    { title: 'Problems', value: stats.problems, icon: Lightbulb, color: 'text-amber-600', link: '/problems', moduleKey: 'problems' },
    { title: 'Laptop Provisioning', value: stats.laptopProvisioning, icon: Laptop, color: 'text-green-600', link: '/my-requests?filter=laptop', moduleKey: 'service_requests', showOnlyIfValue: true }
  ];

  // Filter stat cards based on subscription and optional value conditions
  const statCards = allStatCards.filter(card => {
    const moduleEnabled = isModuleEnabled(card.moduleKey);
    const valueConditionMet = card.showOnlyIfValue ? card.value > 0 : true;
    return moduleEnabled && valueConditionMet;
  });

  // Define quick actions with their module keys
  const allQuickActions = [
    { 
      title: 'Report Incident', 
      description: 'Create a new incident ticket', 
      link: '/incidents', 
      icon: AlertCircle, 
      bgColor: 'bg-red-100 dark:bg-red-900/20', 
      iconColor: 'text-red-600 dark:text-red-400',
      moduleKey: 'incidents'
    },
    { 
      title: 'Service Request', 
      description: 'Submit a service request', 
      link: '/request-catalog', 
      icon: FileText, 
      bgColor: 'bg-blue-100 dark:bg-blue-900/20', 
      iconColor: 'text-blue-600 dark:text-blue-400',
      moduleKey: 'service_requests'
    },
    { 
      title: 'Knowledge Management', 
      description: 'Browse knowledge articles', 
      link: '/knowledge-management', 
      icon: CheckCircle2, 
      bgColor: 'bg-green-100 dark:bg-green-900/20', 
      iconColor: 'text-green-600 dark:text-green-400',
      moduleKey: 'knowledge'
    }
  ];

  // Filter quick actions based on subscription
  const quickActions = allQuickActions.filter(action => isModuleEnabled(action.moduleKey));

  const getStatusBadge = (status) => {
    const variants = {
      open: 'default',
      'in_progress': 'secondary',
      resolved: 'outline',
      closed: 'outline'
    };
    return <Badge variant={variants[status] || 'default'}>{status.replace('_', ' ')}</Badge>;
  };

  const getTypeIcon = (type) => {
    const icons = {
      incident: AlertCircle,
      request: FileText,
      change: GitBranch,
      problem: Lightbulb
    };
    const Icon = icons[type];
    return Icon ? <Icon className="h-4 w-4" /> : null;
  };

  if (loading || subscriptionLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6" data-testid="dashboard-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Dashboard</h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">Overview of your IT service management</p>
          </div>
          {isAdminOrSuper && (
            <Button
              variant="outline"
              onClick={handlePollEmails}
              disabled={polling}
              className="self-start sm:self-auto"
              data-testid="poll-emails-btn"
            >
              {polling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              Poll Emails
            </Button>
          )}
        </div>

        {/* Pending Approvals Widget - Shown prominently for approvers */}
        {canSeeApprovals && pendingApprovals.length > 0 && (
          <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20" data-testid="pending-approvals-card">
            <CardHeader className="p-4 sm:p-6 pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                    <UserCheck className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <CardTitle className="text-base sm:text-lg">Pending Approvals</CardTitle>
                    <CardDescription className="text-xs sm:text-sm">
                      {pendingApprovals.length} item{pendingApprovals.length !== 1 ? 's' : ''} awaiting your approval
                    </CardDescription>
                  </div>
                </div>
                <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                  {pendingApprovals.length} Pending
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              <div className="space-y-3">
                {pendingApprovals.slice(0, 5).map((approval) => (
                  <div
                    key={approval.id}
                    className="flex items-start justify-between p-3 bg-white dark:bg-gray-900 border border-border rounded-lg hover:shadow-sm transition-shadow"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="font-mono text-xs">
                          {approval.task_number}
                        </Badge>
                        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 text-xs">
                          Approval
                        </Badge>
                      </div>
                      <p className="font-medium text-sm truncate">{approval.task_name}</p>
                      {approval.service_request && (
                        <div className="mt-1 text-xs text-muted-foreground space-y-0.5">
                          <p>
                            <span className="font-medium">Request:</span> {approval.service_request.sr_number} - {approval.service_request.catalog_item_name}
                          </p>
                          <p>
                            <span className="font-medium">Requested by:</span> {approval.service_request.requested_by_name}
                            {approval.service_request.requested_for_name && approval.service_request.requested_for_name !== approval.service_request.requested_by_name && (
                              <span> for {approval.service_request.requested_for_name}</span>
                            )}
                          </p>
                          {approval.service_request.justification && (
                            <p className="truncate">
                              <span className="font-medium">Justification:</span> {approval.service_request.justification}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950"
                        onClick={() => openApprovalDialog(approval)}
                        data-testid={`approve-btn-${approval.id}`}
                      >
                        <ThumbsUp className="h-3.5 w-3.5 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950"
                        onClick={() => {
                          setSelectedApproval(approval);
                          setApprovalComments('');
                          setApprovalDialogOpen(true);
                        }}
                        data-testid={`refer-back-btn-${approval.id}`}
                      >
                        <Undo2 className="h-3.5 w-3.5 mr-1" />
                        Refer Back
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                        onClick={() => {
                          setSelectedApproval(approval);
                          setApprovalComments('');
                          setApprovalDialogOpen(true);
                        }}
                        data-testid={`reject-btn-${approval.id}`}
                      >
                        <ThumbsDown className="h-3.5 w-3.5 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
                {pendingApprovals.length > 5 && (
                  <p className="text-xs text-muted-foreground text-center pt-2">
                    + {pendingApprovals.length - 5} more approval{pendingApprovals.length - 5 !== 1 ? 's' : ''} pending
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty state for approvals when none pending but user can see them */}
        {canSeeApprovals && pendingApprovals.length === 0 && !approvalsLoading && (
          <Card className="border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20" data-testid="no-pending-approvals-card">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="font-medium text-sm">All caught up!</p>
                  <p className="text-xs text-muted-foreground">No pending approvals at this time</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Callbacks Widget */}
        {activeCallbacks.length > 0 && (
          <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20" data-testid="active-callbacks-widget">
            <CardHeader className="p-4 sm:p-6 pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                    <PhoneCall className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <CardTitle className="text-base sm:text-lg">Your Callback Requests</CardTitle>
                    <CardDescription className="text-xs">{activeCallbacks.length} active</CardDescription>
                  </div>
                </div>
                <Link to="/request-callback">
                  <Button size="sm" variant="outline" data-testid="view-callbacks-btn">
                    Track Status <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              <div className="space-y-2">
                {activeCallbacks.slice(0, 3).map(cb => {
                  const statusMap = {
                    pending: { label: 'In Queue', color: 'bg-blue-500', icon: Clock },
                    retry_scheduled: { label: 'Retry Scheduled', color: 'bg-amber-500', icon: Timer },
                    assigned: { label: 'Agent Assigned', color: 'bg-indigo-500', icon: User },
                    ringing: { label: 'Calling You', color: 'bg-green-500 animate-pulse', icon: PhoneCall },
                    connected: { label: 'Connected', color: 'bg-green-600', icon: PhoneCall },
                    in_progress: { label: 'In Progress', color: 'bg-indigo-500', icon: User },
                  };
                  const s = statusMap[cb.status] || statusMap.pending;
                  const SIcon = s.icon;
                  return (
                    <Link key={cb.callback_id} to="/request-callback" className="block">
                      <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-blue-100/50 transition-colors" data-testid={`callback-item-${cb.callback_id}`}>
                        <div className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
                        <span className="font-mono text-sm font-medium">{cb.callback_id}</span>
                        <Badge variant="outline" className="text-xs">{s.label}</Badge>
                        {cb.queue_position && cb.status === 'pending' && (
                          <span className="text-xs text-muted-foreground ml-auto">#{cb.queue_position} in queue</span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-3 sm:gap-4 md:gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {statCards.map((card) => {
            const Icon = card.icon;
            const isLaptopProvisioning = card.title === 'Laptop Provisioning';
            return (
              <Link key={card.title} to={card.link} className="block h-full">
                <Card 
                  className={`hover:shadow-md transition-shadow cursor-pointer h-full ${
                    isLaptopProvisioning ? 'border-green-200 dark:border-green-800 bg-green-50/30 dark:bg-green-950/10' : ''
                  }`} 
                  data-testid={`stat-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0 p-3 sm:p-6">
                    <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground truncate pr-2">
                      {isLaptopProvisioning ? 'Provisioning' : card.title}
                    </CardTitle>
                    <Icon className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${card.color}`} />
                  </CardHeader>
                  <CardContent className="p-3 sm:p-6 pt-0">
                    <div className="text-2xl sm:text-3xl font-semibold">{card.value}</div>
                    <p className="text-xs text-muted-foreground mt-1 hidden sm:block">
                      {isLaptopProvisioning ? 'In progress' : 'Total active'}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        <div className={`grid gap-4 sm:gap-6 ${!isItilUser ? 'lg:grid-cols-2' : ''}`}>
          <Card data-testid="recent-tickets-card">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-base sm:text-lg">Recent Tickets</CardTitle>
              <CardDescription className="text-xs sm:text-sm">Your latest ticket activity</CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              {recentTickets.length === 0 ? (
                <p className="text-muted-foreground text-sm">No recent tickets</p>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {recentTickets.map((ticket) => {
                    // Determine the link based on ticket type - use direct routes
                    const ticketLink = ticket.type === 'incident' ? `/incidents/${ticket.id}` :
                                       ticket.type === 'request' ? `/my-requests?view=${ticket.id}` :
                                       ticket.type === 'change' ? `/changes/${ticket.id}` :
                                       ticket.type === 'problem' ? `/problems/${ticket.id}` : '#';
                    return (
                      <Link
                        key={ticket.id}
                        to={ticketLink}
                        className="flex items-start justify-between p-2 sm:p-3 border border-border rounded-md hover:bg-accent transition-colors cursor-pointer"
                        data-testid={`recent-ticket-${ticket.id}`}
                      >
                        <div className="flex items-start gap-2 sm:gap-3 min-w-0 flex-1">
                          <div className="mt-0.5 hidden sm:block">
                            {getTypeIcon(ticket.type)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-xs sm:text-sm truncate">{ticket.title}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              <Badge variant="outline" className="font-mono text-[10px] mr-2">
                                {ticket.incident_id || ticket.change_id || ticket.problem_id || ticket.request_id || ticket.id}
                              </Badge>
                              <span className="capitalize">{ticket.type}</span>
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          {getStatusBadge(ticket.status)}
                          <p className="text-xs text-muted-foreground mt-1 capitalize hidden sm:block">{ticket.priority}</p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions - Only show for End Users */}
          {!isItilUser && (
            <Card data-testid="quick-actions-card">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">Quick Actions</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Common tasks and shortcuts</CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                <div className="grid gap-2 sm:gap-3">
                  {quickActions.map((action) => {
                    const Icon = action.icon;
                    return (
                      <Link key={action.title} to={action.link} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 border border-border rounded-md hover:bg-accent transition-colors">
                        <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded-md ${action.bgColor} flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${action.iconColor}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-xs sm:text-sm">{action.title}</p>
                          <p className="text-xs text-muted-foreground hidden sm:block">{action.description}</p>
                        </div>
                      </Link>
                    );
                  })}
                  {quickActions.length === 0 && (
                    <p className="text-muted-foreground text-sm">No quick actions available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Created by Me Section - For ITIL users */}
        {isItilUser && isModuleEnabled('incidents') && (
          <Card data-testid="created-by-me-card">
            <CardHeader className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Created by Me
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm">Incidents you have created</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <Select
                    value={myIncidentsFilter.status}
                    onValueChange={(value) => setMyIncidentsFilter(prev => ({ ...prev, status: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger className="w-[120px] h-8 text-xs">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={myIncidentsFilter.priority}
                    onValueChange={(value) => setMyIncidentsFilter(prev => ({ ...prev, priority: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger className="w-[120px] h-8 text-xs">
                      <SelectValue placeholder="Priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priority</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              {myCreatedIncidents.length === 0 ? (
                <p className="text-muted-foreground text-sm">No incidents created by you{myIncidentsFilter.status || myIncidentsFilter.priority ? ' with current filters' : ''}</p>
              ) : (
                <div className="space-y-3">
                  {myCreatedIncidents.slice(0, 10).map((incident) => (
                    <Link
                      key={incident.id}
                      to={`/incidents/${incident.id}`}
                      className="flex items-start justify-between p-3 border border-border rounded-md hover:bg-accent transition-colors"
                    >
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <AlertCircle className="h-4 w-4 mt-0.5 text-red-600 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{incident.title}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            <Badge variant="outline" className="font-mono text-[10px] mr-1">{incident.id}</Badge>
                            • Created {new Date(incident.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2 space-y-1">
                        {getStatusBadge(incident.status)}
                        <p className="text-xs text-muted-foreground capitalize">{incident.priority}</p>
                      </div>
                    </Link>
                  ))}
                  {myCreatedIncidents.length > 10 && (
                    <p className="text-xs text-muted-foreground text-center">
                      Showing 10 of {myCreatedIncidents.length} incidents
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Approval Decision Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-amber-600" />
              Approval Decision
            </DialogTitle>
            <DialogDescription>
              Review the request details and make your decision.
            </DialogDescription>
          </DialogHeader>
          
          {selectedApproval && (
            <div className="space-y-3">
              {/* Request Details Box */}
              <div className="p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="grid gap-2.5 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Task:</span>
                    <span className="font-medium">{selectedApproval.task_number}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Name:</span>
                    <span className="font-medium">{selectedApproval.task_name}</span>
                  </div>
                  {selectedApproval.service_request && (
                    <>
                      <div className="pt-2.5 mt-1 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                        <span className="text-muted-foreground">Service Request:</span>
                        <span className="font-medium">{selectedApproval.service_request.sr_number}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Item:</span>
                        <span className="font-medium">{selectedApproval.service_request.catalog_item_name}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Requested by:</span>
                        <span className="font-medium">{selectedApproval.service_request.requested_by_name}</span>
                      </div>
                      {selectedApproval.service_request.justification && (
                        <div className="pt-2.5 mt-1 border-t border-gray-200 dark:border-gray-700">
                          <span className="text-muted-foreground">Justification:</span>
                          <p className="font-medium mt-2 text-xs p-3 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                            {selectedApproval.service_request.justification}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Comments Section */}
              <div className="p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <Label htmlFor="approval-comments" className="text-sm">Comments {selectedApproval && '(Required for Refer Back/Reject)'}</Label>
                <Textarea
                  id="approval-comments"
                  placeholder="Add comments about your decision..."
                  value={approvalComments}
                  onChange={(e) => setApprovalComments(e.target.value)}
                  rows={3}
                  className="resize-none mt-2 border-gray-200 dark:border-gray-700"
                />
              </div>
              
              {/* Expedite Option - Only for Approve */}
              <div className="flex items-start gap-3 p-4 bg-white dark:bg-gray-900 rounded-lg border border-orange-300 dark:border-orange-700">
                <Checkbox
                  id="expedite-checkbox"
                  checked={isExpedited}
                  onCheckedChange={handleExpediteChange}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <Label htmlFor="expedite-checkbox" className="flex items-center gap-2 cursor-pointer">
                    <Zap className="h-4 w-4 text-orange-600 shrink-0" />
                    <span className="font-medium">Expedite this request</span>
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Mark as high priority for faster fulfillment
                  </p>
                </div>
                {expediteConfirmed && (
                  <Badge className="bg-orange-500 text-white shrink-0">
                    <Zap className="h-3 w-3 mr-1" />
                    Expedited
                  </Badge>
                )}
              </div>
              
              {/* Decision descriptions */}
              <div className="text-xs space-y-1.5 p-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <p><span className="font-semibold text-green-600">Approve:</span> Good to process further</p>
                <p><span className="font-semibold text-amber-600">Refer Back:</span> Request modifications from submitter</p>
                <p><span className="font-semibold text-red-600">Reject:</span> Request shall not be processed further</p>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setApprovalDialogOpen(false)}
              disabled={processingApproval}
              className="sm:mr-auto"
            >
              Cancel
            </Button>
            <div className="flex gap-2 sm:gap-3">
              <Button
                variant="destructive"
                onClick={() => handleApprovalDecision('rejected')}
                disabled={processingApproval || !approvalComments.trim()}
                className="bg-red-600 hover:bg-red-700"
                title={!approvalComments.trim() ? 'Comments required for rejection' : ''}
              >
                {processingApproval ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ThumbsDown className="h-4 w-4 mr-2" />
                )}
                Reject
              </Button>
              <Button
                onClick={() => handleApprovalDecision('refer_back')}
                disabled={processingApproval || !approvalComments.trim()}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                title={!approvalComments.trim() ? 'Comments required when referring back' : ''}
              >
                {processingApproval ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Undo2 className="h-4 w-4 mr-2" />
                )}
                Refer Back
              </Button>
              <Button
                onClick={() => handleApprovalDecision('approved')}
                disabled={processingApproval}
                className={expediteConfirmed ? "bg-orange-600 hover:bg-orange-700" : "bg-green-600 hover:bg-green-700"}
              >
                {processingApproval ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : expediteConfirmed ? (
                  <Zap className="h-4 w-4 mr-2" />
                ) : (
                  <ThumbsUp className="h-4 w-4 mr-2" />
                )}
                {expediteConfirmed ? 'Approve & Expedite' : 'Approve'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expedite Confirmation Dialog */}
      <Dialog open={showExpediteConfirm} onOpenChange={setShowExpediteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Zap className="h-5 w-5" />
              Expedite Request?
            </DialogTitle>
          </DialogHeader>
          
          <Alert className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <AlertTitle className="text-orange-800 dark:text-orange-300">Cost Implications</AlertTitle>
            <AlertDescription className="text-orange-700 dark:text-orange-400">
              Expediting this request will increase the cost of fulfillment as teams will work on prioritizing its completion over other requests.
            </AlertDescription>
          </Alert>
          
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>When you expedite a request:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>All associated tasks will be marked as high priority</li>
              <li>Fulfillment teams will prioritize this over standard requests</li>
              <li>Additional resources may be allocated</li>
              <li>This may affect SLA targets for other requests</li>
            </ul>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowExpediteConfirm(false);
                setIsExpedited(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmExpedite}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Zap className="h-4 w-4 mr-2" />
              Yes, Expedite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};